/**
 * LessonsTab — self-contained lessons+schedule workspace.
 *
 * Extracted from StudentDashboard.tsx 2026-07-01.
 * Zero imports from StudentDashboard. All helpers duplicated locally.
 * Props: data from BFF V2 (schedule, next_lesson, calendar, activities).
 */
import React from 'react'
import { useNavigate } from 'react-router-dom'
import type { BffV2Response } from '../../types/bff-v2'

// ══════════════════════════════════════════════════════════════════
// Helpers
// ══════════════════════════════════════════════════════════════════

const formatTimeStr = (t: string): string => {
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const hour = h % 12 || 12
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`
}

const formatRelativeTime = (ts: string): string => {
  try {
    const d = new Date(ts)
    const diffHrs = Math.floor((Date.now() - d.getTime()) / 3600000)
    if (diffHrs < 24) return `${diffHrs || 1}h ago`
    const diffDays = Math.floor(diffHrs / 24)
    if (diffDays < 7) return `${diffDays}d ago`
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch { return '' }
}

const parseDay = (date: string): number => parseInt(date.split(' ')[1] || '0', 10)

// ══════════════════════════════════════════════════════════════════
// Static data
// ══════════════════════════════════════════════════════════════════

const DAY_HEADERS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const
const DAY_SLOTS = [
  { day: 'Mon', slots: 8, level: 'y' as const },
  { day: 'Tue', slots: 6, level: 'y' as const },
  { day: 'Wed', slots: 2, level: 'w' as const },
  { day: 'Thu', slots: 7, level: 'y' as const },
  { day: 'Fri', slots: 0, level: 'n' as const },
]

// ══════════════════════════════════════════════════════════════════
// Shared sub-component
// ══════════════════════════════════════════════════════════════════

const SectionHead: React.FC<{ title: string; action?: { label: string; onClick: () => void } }> = ({ title, action }) => (
  <div className="flex items-center justify-between mt-[24px] mb-[14px]">
    <h2 className="text-[15px] font-bold text-[var(--color-text-primary)]">{title}</h2>
    {action && (
      <button onClick={action.onClick}
              className="text-[12px] font-semibold text-[var(--color-teal)] hover:opacity-75 cursor-pointer">
        {action.label} →
      </button>
    )}
  </div>
)

// ══════════════════════════════════════════════════════════════════
// LessonsTab
// ══════════════════════════════════════════════════════════════════

const LessonsTab: React.FC<{ data: BffV2Response }> = ({ data }) => {
  const { schedule, next_lesson, calendar, activities } = data
  const navigate = useNavigate()

  // Flatten schedule into carousel cards
  const upcomingCards = (schedule || []).flatMap(day =>
    (day.items || []).map(item => ({
      ...item,
      dayName: day.day,
      dateStr: day.date,
      dateNum: parseDay(day.date),
      timeFormatted: formatTimeStr(item.time),
    }))
  )

  // Past practice from activity feed
  const pastLessons = (activities || [])
    .filter(a => a.type === 'practice')
    .slice(0, 5)
    .map(a => ({
      ...a,
      relDate: formatRelativeTime(a.timestamp),
    }))

  const calMonth = calendar?.month || new Date().toLocaleString('en-US', { month: 'long' })
  const calYear = calendar?.year || new Date().getFullYear()
  const calDays = calendar?.days || []

  return (
    <>
      {/* ── Upcoming Lessons Carousel ── */}
      <SectionHead title="Upcoming Lessons" />
      {upcomingCards.length > 0 ? (
        <div className="flex gap-[10px] overflow-x-auto scroll-snap-x-mandatory pb-[4px] mb-[14px]">
          {upcomingCards.slice(0, 5).map((item, i) => (
            <div key={item.id}
                 className="flex-shrink-0 w-[clamp(165px,22vw,200px)] scroll-snap-align-start
                            bg-[var(--color-card)] border border-[var(--color-border-light)]
                            rounded-[var(--radius-r)] p-[15px] cursor-pointer
                            transition-all duration-[.18s] relative overflow-hidden
                            hover:-translate-y-[2px] hover:shadow-[var(--shadow-sh2)]"
                 onClick={() => navigate('/dashboard/lessons')}
            >
              <div className="absolute top-0 left-0 right-0 h-[3px]"
                   style={{ background: i === 0 ? 'var(--color-cyan)' : 'var(--color-amber-accent)' }} />
              <div className="text-[28px] font-black" style={{ color: i === 0 ? 'var(--color-cyan)' : 'var(--color-amber-accent)' }}>
                {item.dateNum}
              </div>
              <div className="text-[13px] font-bold text-[var(--color-text-primary)] mt-[6px]">{item.title}</div>
              <div className="text-[12px] text-[var(--color-text-secondary)] mt-[2px]">{item.teacher}</div>
              <div className="text-[11px] text-[var(--color-text-tertiary)] mt-[1px]">{item.dayName} · {item.timeFormatted}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex-shrink-0 w-[200px] bg-[var(--color-card2)] border border-[var(--color-border-light)]
                        rounded-[var(--radius-r)] p-[15px] flex items-center justify-center mb-[14px]">
          <span className="text-[13px] text-[var(--color-text-tertiary)]">No upcoming lessons</span>
        </div>
      )}

      {/* ── Next Lesson + Schedule ── */}
      <div className="bg-[var(--color-card)] border border-[rgba(91,164,181,.2)]
                      rounded-[var(--radius-r)] p-[18px] shadow-[var(--shadow-sh0)] mb-[14px] cursor-pointer
                      hover:shadow-[var(--shadow-sh2)] transition-all duration-[.18s]"
           onClick={() => navigate('/dashboard/lessons')}>
        <div className="flex items-center gap-[8px] text-[11px] font-semibold text-[var(--color-text-tertiary)]
                        uppercase tracking-[.5px] mb-[12px]">
          📅 Schedule & Availability
          <span className="ml-auto text-[10px] text-[var(--color-text-tertiary)] font-medium">Open →</span>
        </div>
        <div className="flex items-center gap-[16px]">
          <div className="flex-1">
            {next_lesson ? (
              <>
                <div className="text-[13px] font-bold text-[var(--color-text-primary)]">
                  Next lesson: {next_lesson.title}
                </div>
                <div className="text-[11px] text-[var(--color-text-tertiary)] mt-[2px]">
                  {next_lesson.teacher_name} · {next_lesson.datetime ? formatTimeStr(next_lesson.datetime.split(' ')[1] || '') : ''} · {next_lesson.duration_min} min
                </div>
              </>
            ) : (
              <div className="text-[13px] font-bold text-[var(--color-text-primary)]">No upcoming lessons</div>
            )}
          </div>
          {next_lesson && (
            <div className="text-right">
              <div className="text-[28px] font-black" style={{ color: 'var(--color-cyan)' }}>
                {next_lesson.datetime
                  ? parseDay(new Date(next_lesson.datetime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }))
                  : '—'}
              </div>
              <div className="text-[11px] text-[var(--color-text-tertiary)] font-semibold">
                {next_lesson.datetime
                  ? new Date(next_lesson.datetime).toLocaleDateString('en-US', { month: 'short' }).toUpperCase()
                  : ''}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Recent Activity ── */}
      <SectionHead title="Recent Activity" />
      {pastLessons.length > 0 ? pastLessons.map(lesson => (
        <div key={lesson.id}
             className="flex items-center gap-[12px] p-[14px] bg-[var(--color-card)]
                        border border-[var(--color-border-light)] rounded-[var(--radius-r)]
                        cursor-pointer transition-all duration-[.18s] shadow-[var(--shadow-sh0)]
                        hover:-translate-y-[1px] hover:shadow-[var(--shadow-sh2)] mb-[7px]">
          <div className="flex-1 flex flex-col gap-[2px]">
            <strong className="text-[14px] text-[var(--color-text-primary)]">{lesson.title}</strong>
            <span className="text-[12px] text-[var(--color-text-secondary)]">
              {lesson.subtitle} · {lesson.relDate}
            </span>
          </div>
          <button className="px-[12px] py-[6px] bg-[var(--color-teal)] text-white text-[12px] font-semibold
                             rounded-[var(--radius-r-sm)] hover:opacity-90 transition-all duration-[.18s]">
            Practice Again
          </button>
        </div>
      )) : (
        <div className="p-[14px] bg-[var(--color-card2)] border border-[var(--color-border-light)]
                        rounded-[var(--radius-r)] flex items-center justify-center mb-[7px]">
          <span className="text-[13px] text-[var(--color-text-tertiary)]">No recent activity</span>
        </div>
      )}

      {/* ── Calendar ── */}
      <SectionHead title={`${calMonth} ${calYear}`} />
      <div className="bg-[var(--color-card)] border border-[var(--color-border-light)]
                      rounded-[var(--radius-r)] p-[14px] shadow-[var(--shadow-sh0)] mb-[14px]">
        <div className="grid grid-cols-7 gap-[2px]">
          {DAY_HEADERS.map(dh => (
            <div key={dh} className="text-[9px] text-[var(--color-text-tertiary)] text-center font-semibold p-[3px] uppercase">{dh}</div>
          ))}
          {calDays.map((cd: any, i: number) => (
            <div key={i}
                 className={`aspect-square flex items-center justify-center text-[11px] font-medium
                            rounded-[8px] cursor-pointer transition-all duration-[.15s]
                            relative hover:bg-[var(--color-card2)]
                            ${cd.is_today ? 'bg-[var(--color-cyan)] text-white font-bold' : 'text-[var(--color-text-secondary)]'}
                            ${!cd.is_current_month ? 'text-[var(--color-text-placeholder)]' : ''}`}>
              {cd.date}
              {cd.has_lesson && (
                <span className={`absolute bottom-[3px] w-[4px] h-[4px] rounded-full
                                 ${cd.is_today ? 'bg-white' : 'bg-[var(--color-cyan)]'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Teacher Availability ── */}
      <SectionHead title="Teacher Availability" />
      <div className="bg-[var(--color-card)] border border-[var(--color-border-light)]
                      rounded-[var(--radius-r)] p-[14px] shadow-[var(--shadow-sh0)] mb-[60px]">
        <div className="grid grid-cols-5 gap-[3px] mt-[6px]">
          {DAY_SLOTS.map(ds => (
            <div key={ds.day}
                 className={`text-center p-[8px] rounded-[12px] text-[10px] font-semibold
                            ${ds.level === 'y' ? 'bg-[rgba(0,167,142,.12)] border border-[rgba(0,167,142,.2)] text-[var(--color-jade)]' : ''}
                            ${ds.level === 'w' ? 'bg-[rgba(201,168,76,.12)] border border-[rgba(201,168,76,.2)] text-[var(--color-gold)]' : ''}
                            ${ds.level === 'n' ? 'bg-[rgba(100,116,139,.05)] border border-[rgba(100,116,139,.08)] text-[var(--color-text-tertiary)]' : ''}`}>
              {ds.day}<br />{ds.slots > 0 ? `${ds.slots} slots` : 'Full'}
            </div>
          ))}
        </div>
        <div className="text-[10px] text-[var(--color-text-placeholder)] text-center mt-[10px]">
          Sample availability · Real data coming soon
        </div>
      </div>
    </>
  )
}

export default LessonsTab
